import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md'
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    // Handle escape key to close
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  // Apply different max-width based on size prop
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-839">
      <div ref={modalRef} className={`bg-gray-800 border border-gray-700 rounded-lg shadow-xl w-full ${sizeClasses[size]} animate-modal-in`} data-id="element-840">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700" data-id="element-841">
          <h3 className="text-lg font-medium text-gray-100" data-id="element-842">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-full p-1" aria-label="Close" data-id="element-843">
            <XIcon size={20} data-id="element-844" />
          </button>
        </div>
        {children}
      </div>
    </div>;
}